/**
 * Nutritional Comparison Section
 * Handles the nutritional comparison display between Rebelle and competitor products
 * Supports pack mode with multiple products navigation
 */

(function () {
  'use strict';

  class NutritionalComparison {
    constructor(section) {
      this.section = section;
      this.hasPack = this.section.dataset.hasPack === 'true';
      this.swiper = null;
      this.navItems = null;
      this.contentWrappers = null;
      this.currentIndex = 0;
      this.packColors = [];
      this.adjacentSeparators = { prev: null, next: null };

      // Parse pack colors
      if (this.hasPack && this.section.dataset.packColors) {
        this.packColors = this.section.dataset.packColors.split('|');
      }

      this.init();
    }

    init() {
      if (this.hasPack) {
        this.initPackMode();
      }
      console.log('Nutritional Comparison section initialized', { hasPack: this.hasPack });
    }

    initPackMode() {
      // Get navigation items and content wrappers
      this.navItems = this.section.querySelectorAll('.section-nutritional-comparison__nav-item');
      this.contentWrappers = this.section.querySelectorAll('[data-product-content]');

      if (!this.navItems.length || !this.contentWrappers.length) {
        console.warn('Pack mode elements not found');
        return;
      }

      // Find adjacent separators
      this.findAdjacentSeparators();

      // Initialize Swiper
      this.initSwiper();

      // Add click listeners to navigation items
      this.navItems.forEach((item, index) => {
        item.addEventListener('click', () => {
          this.switchToProduct(index);
        });
      });
    }

    findAdjacentSeparators() {
      // Get the parent shopify-section wrapper
      const wrapper = this.section.closest('.shopify-section');
      if (!wrapper) return;

      // Find previous separator
      let prevWrapper = wrapper.previousElementSibling;
      while (prevWrapper) {
        const separator = prevWrapper.querySelector('[data-section-type="w-separator"]');
        if (separator) {
          this.adjacentSeparators.prev = separator;
          break;
        }
        prevWrapper = prevWrapper.previousElementSibling;
      }

      // Find next separator
      let nextWrapper = wrapper.nextElementSibling;
      while (nextWrapper) {
        const separator = nextWrapper.querySelector('[data-section-type="w-separator"]');
        if (separator) {
          this.adjacentSeparators.next = separator;
          break;
        }
        nextWrapper = nextWrapper.nextElementSibling;
      }
    }

    initSwiper() {
      const swiperContainer = this.section.querySelector('.nutritional-comparison-swiper');

      if (!swiperContainer) {
        console.warn('Swiper container not found');
        return;
      }

      // Check if Swiper is available
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper library not loaded');
        return;
      }

      this.swiper = new Swiper(swiperContainer, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        centeredSlides: false,
        freeMode: true,
        pagination: {
          el: '.nutritional-comparison-swiper__pagination',
          clickable: true,
        },
        breakpoints: {
          0: {
            slidesPerView: 'auto',
            spaceBetween: 6,
            centeredSlides: false,
          },
          990: {
            slidesPerView: 'auto',
            spaceBetween: 8,
            centeredSlides: false,
          }
        }
      });
    }

    switchToProduct(index) {
      if (index === this.currentIndex) return;

      // Update navigation active state
      this.navItems.forEach((item, i) => {
        if (i === index) {
          item.classList.add('section-nutritional-comparison__nav-item--active');
        } else {
          item.classList.remove('section-nutritional-comparison__nav-item--active');
        }
      });

      // Switch content wrappers
      this.contentWrappers.forEach((wrapper, i) => {
        const wrapperIndex = parseInt(wrapper.dataset.productContent, 10);
        if (wrapperIndex === index) {
          wrapper.classList.remove('section-nutritional-comparison__content-wrapper--hidden');
        } else {
          wrapper.classList.add('section-nutritional-comparison__content-wrapper--hidden');
        }
      });

      // Change section background color
      if (this.packColors[index]) {
        const newColor = this.packColors[index];
        this.section.style.backgroundColor = newColor;

        // Change adjacent separators --separator-color CSS variable
        if (this.adjacentSeparators.prev) {
          this.adjacentSeparators.prev.style.setProperty('--separator-color', newColor);
        }
        if (this.adjacentSeparators.next) {
          this.adjacentSeparators.next.style.setProperty('--separator-color', newColor);
        }
      }

      this.currentIndex = index;

      // Slide swiper to active item if available
      if (this.swiper) {
        this.swiper.slideTo(index);
      }
    }
  }

  // Initialize all sections on page load
  document.addEventListener('DOMContentLoaded', function () {
    const sections = document.querySelectorAll('[data-section-type="w-nutritional-comparison"]');
    sections.forEach(section => {
      new NutritionalComparison(section);
    });

    // Hide adjacent separators if nutritional comparison is empty
    const emptySections = document.querySelectorAll('[data-section-type="w-nutritional-comparison-empty"]');
    emptySections.forEach(emptySection => {
      // Get the parent shopify-section wrapper
      const wrapper = emptySection.closest('.shopify-section');
      if (!wrapper) return;

      // Find previous separator wrapper
      let prevWrapper = wrapper.previousElementSibling;
      while (prevWrapper) {
        const separator = prevWrapper.querySelector('[data-section-type="w-separator"]');
        if (separator) {
          prevWrapper.style.display = 'none';
          break;
        }
        prevWrapper = prevWrapper.previousElementSibling;
      }

      // Find next separator wrapper
      let nextWrapper = wrapper.nextElementSibling;
      while (nextWrapper) {
        const separator = nextWrapper.querySelector('[data-section-type="w-separator"]');
        if (separator) {
          nextWrapper.style.display = 'none';
          break;
        }
        nextWrapper = nextWrapper.nextElementSibling;
      }
    });
  });

  // Shopify Theme Editor support
  if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      if (event.target.querySelector('[data-section-type="w-nutritional-comparison"]')) {
        new NutritionalComparison(event.target);
      }
    });
  }
})();
